import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Typography, Space, Select } from 'antd';
import { PhoneOutlined, LockOutlined, GlobalOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { customerAPI } from '../services/api';
import { countryCodes, getDefaultCountryCode, saveCountryCodePreference, validatePhoneNumber } from '../utils/countryCodes';

const { Title, Text } = Typography;
const { Option } = Select;

// Force Firebase to use reCAPTCHA v2 instead of Enterprise
const initializeRecaptchaV2 = () => {
  if (typeof window !== 'undefined') {
    // Set global recaptcha options before Firebase Auth initialization
    window.recaptchaOptions = {
      useRecaptchaEnterprise: false
    };

    // Also set it on the global grecaptcha object if it exists
    if (window.grecaptcha && window.grecaptcha.enterprise) {
      window.grecaptcha.enterprise.ready = undefined;
    }
  }
};

// Call this before any Firebase Auth operations
initializeRecaptchaV2();

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

const Login = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState(getDefaultCountryCode() || '');
  const [confirmationResult, setConfirmationResult] = useState(null);

  useEffect(() => {
    // Check if already logged in
    if (localStorage.getItem('token')) {
      navigate('/bookings');
    }
  }, [navigate]);

  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {
          // reCAPTCHA solved, allow signInWithPhoneNumber
        },
      });
    }
  };

  const handleSendOTP = async (values) => {
    try {
      setLoading(true);
      const phone = values.phoneNumber;

      // Validate country code selection
      if (!countryCode) {
        message.error('Please select a country code');
        setLoading(false);
        return;
      }

      // Validate phone number format
      if (!validatePhoneNumber(phone, countryCode)) {
        message.error('Please enter a valid phone number for the selected country');
        setLoading(false);
        return;
      }

      // Format phone number with country code
      const formattedPhone = phone.startsWith('+') ? phone : `${countryCode}${phone}`;

      // Pre-check account: Check if Firebase user exists or create from booking
      try {
        await customerAPI.checkAccount({ phoneNumber: formattedPhone });
        // Account exists or was created successfully, continue with auth
      } catch (error) {
        // Check if the error is specifically about no bookings found
        if (error.message && error.message.includes('No bookings found')) {
          message.error(error.message);
          setLoading(false);
          return;
        }
        // For any other errors (network issues, etc.), continue with normal auth flow
        console.log('Account check failed, continuing with auth:', error);
      }

      setPhoneNumber(formattedPhone);

      // Save user's country code preference
      saveCountryCodePreference(countryCode);

      setupRecaptcha();
      const appVerifier = window.recaptchaVerifier;

      const confirmationResult = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
      setConfirmationResult(confirmationResult);
      setStep('otp');
      message.success('OTP sent successfully!');
    } catch (error) {
      console.error('Error sending OTP:', error);
      message.error('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (values) => {
    try {
      setLoading(true);
      const otp = values.otp;

      // Verify OTP with Firebase
      const result = await confirmationResult.confirm(otp);
      const idToken = await result.user.getIdToken();

      // Send to backend for authentication
      const response = await customerAPI.login({
        idToken,
        phoneNumber,
      });

      if (response.success) {
        localStorage.setItem('token', response.data.token);
        message.success('Login successful!');
        navigate('/bookings');
      } else {
        message.error(response.message || 'Login failed');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      message.error('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('phone');
    setConfirmationResult(null);
    form.resetFields(['otp']);
  };

  return (
    <div className="page-container">
      <Card className="main-card" style={{ width: 400, maxWidth: '90vw' }}>
        <div className="card-content">
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <img
              src="/logo.png"
              alt="Xoom Drives"
              style={{ height: 80, marginBottom: 8 }}
            />
            <Text type="secondary">
              {step === 'phone' ? 'Enter your phone number to continue' : 'Enter the OTP sent to your phone'}
            </Text>
          </div>

          {step === 'phone' ? (
            <Form form={form} onFinish={handleSendOTP} layout="vertical">
              <Form.Item
                name="countryCode"
                label="Country Code"
                rules={[
                  { required: true, message: 'Please select your country code' },
                ]}
                style={{ marginBottom: 16 }}
              >
                <Select
                  value={countryCode}
                  onChange={(value) => {
                    setCountryCode(value);
                    saveCountryCodePreference(value);
                    form.setFieldsValue({ countryCode: value });
                  }}
                  placeholder="Select country code"
                  size="large"
                  style={{ borderRadius: 8 }}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  suffixIcon={<GlobalOutlined />}
                >
                  {countryCodes.map((country) => (
                    <Option key={`${country.value}-${country.country}`} value={country.value}>
                      {country.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="phoneNumber"
                label="Phone Number"
                rules={[
                  { required: true, message: 'Please enter your phone number' },
                  {
                    validator: (_, value) => {
                      if (!value || !countryCode) {
                        return Promise.resolve();
                      }
                      if (validatePhoneNumber(value, countryCode)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Please enter a valid phone number for the selected country'));
                    },
                  },
                ]}
              >
                <Input
                  prefix={<PhoneOutlined />}
                  placeholder={countryCode ? `Enter your phone number (without ${countryCode})` : 'Select country code first'}
                  size="large"
                  style={{ borderRadius: 8 }}
                  disabled={!countryCode}
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  size="large"
                  className="primary-button"
                >
                  Send OTP
                </Button>
              </Form.Item>
            </Form>
          ) : (
            <Form form={form} onFinish={handleVerifyOTP} layout="vertical">
              <Form.Item
                name="otp"
                label={`OTP sent to ${phoneNumber}`}
                rules={[
                  { required: true, message: 'Please enter the OTP' },
                  { len: 6, message: 'OTP must be 6 digits' },
                ]}
              >
                <Input
                  prefix={<LockOutlined />}
                  placeholder="Enter 6-digit OTP"
                  size="large"
                  style={{ borderRadius: 8 }}
                  maxLength={6}
                />
              </Form.Item>

              <Form.Item>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                    size="large"
                    className="primary-button"
                  >
                    Verify & Login
                  </Button>

                  <Button
                    type="link"
                    onClick={handleBack}
                    block
                    style={{ color: '#2e5ec3' }}
                  >
                    Change Phone Number
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          )}

          <div id="recaptcha-container"></div>
        </div>
      </Card>
    </div>
  );
};

export default Login;